import { useState, useEffect } from 'react';
import { locationsApi } from '../services/api';
import { cacheManager } from '../services/cacheManager';

/**
 * Manages hierarchical location selection (Site > Bâtiment > Étage > Bureau)
 * with offline fallback via cached location tree.
 */
export function useCascadeLocation() {
  const [sites, setSites] = useState([]);
  const [batiments, setBatiments] = useState([]);
  const [etages, setEtages] = useState([]);
  const [bureaux, setBureaux] = useState([]);

  const [selectedSite, setSelectedSite] = useState('');
  const [selectedBatiment, setSelectedBatiment] = useState('');
  const [selectedEtage, setSelectedEtage] = useState('');
  const [selectedBureau, setSelectedBureau] = useState('');

  const [cachedLocationTree, setCachedLocationTree] = useState(null);

  const findChildrenInTree = (tree, parentId) => {
    for (const node of tree) {
      if (node.id === parentId) return node.children || [];
      if (node.children?.length) {
        const found = findChildrenInTree(node.children, parentId);
        if (found.length > 0) return found;
      }
    }
    return [];
  };

  const loadChildren = async (parentId, setter) => {
    if (cachedLocationTree?.length) {
      const children = findChildrenInTree(cachedLocationTree, parentId);
      setter(children.map(({ id, nom, type_location }) => ({ id, nom, type_location })));
      return;
    }
    try {
      const response = await locationsApi.getByParent(parentId);
      setter(response.data);
    } catch {
      try {
        const cached = await cacheManager.getCachedData('/api/locations/tree');
        if (cached?.data) {
          const children = findChildrenInTree(cached.data, parentId);
          setter(children.map(({ id, nom, type_location }) => ({ id, nom, type_location })));
        } else {
          setter([]);
        }
      } catch {
        setter([]);
      }
    }
  };

  const loadSites = async () => {
    try {
      const response = await locationsApi.getSites();
      setSites(response.data);
      setCachedLocationTree(null);
    } catch {
      try {
        const cachedTree = await cacheManager.getCachedData('/api/locations/tree');
        if (cachedTree?.data?.length) {
          setCachedLocationTree(cachedTree.data);
          setSites(cachedTree.data.map(({ id, nom, type_location, adresse, ville }) => ({
            id, nom, type_location, adresse, ville
          })));
        } else {
          const cached = await cacheManager.getCachedData('/api/locations/sites');
          if (cached?.data?.length) setSites(cached.data);
        }
      } catch {
        // leave sites empty
      }
    }
  };

  // Cascade effects
  useEffect(() => {
    if (selectedSite) {
      loadChildren(selectedSite, setBatiments);
    } else {
      setBatiments([]);
    }
    setSelectedBatiment('');
    setSelectedEtage('');
    setSelectedBureau('');
    setEtages([]);
    setBureaux([]);
  }, [selectedSite, cachedLocationTree]);

  useEffect(() => {
    if (selectedBatiment) {
      loadChildren(selectedBatiment, setEtages);
    } else {
      setEtages([]);
    }
    setSelectedEtage('');
    setSelectedBureau('');
    setBureaux([]);
  }, [selectedBatiment, cachedLocationTree]);

  useEffect(() => {
    if (selectedEtage) {
      loadChildren(selectedEtage, setBureaux);
    } else {
      setBureaux([]);
    }
    setSelectedBureau('');
  }, [selectedEtage, cachedLocationTree]);

  /** The most specific selected location ID */
  const selectedLocationId = selectedBureau || selectedEtage || selectedBatiment || selectedSite || null;

  /** Human-readable path of the current selection */
  const locationPath = [
    sites.find(s => s.id === selectedSite)?.nom,
    batiments.find(b => b.id === selectedBatiment)?.nom,
    etages.find(e => e.id === selectedEtage)?.nom,
    bureaux.find(b => b.id === selectedBureau)?.nom,
  ].filter(Boolean).join(' > ');

  /** Restore selections from a known locationId by traversing up the hierarchy */
  const restoreFromLocationId = async (locationId) => {
    if (!locationId) return;
    try {
      const path = [];
      let currentId = locationId;
      while (currentId) {
        const res = await locationsApi.getById(currentId);
        path.unshift(res.data);
        currentId = res.data.parent_id;
      }
      for (const loc of path) {
        if (loc.type_location === 'site') {
          setSelectedSite(loc.id);
          const bat = await locationsApi.getByParent(loc.id);
          setBatiments(bat.data);
        } else if (loc.type_location === 'batiment') {
          setSelectedBatiment(loc.id);
          const et = await locationsApi.getByParent(loc.id);
          setEtages(et.data);
        } else if (loc.type_location === 'etage') {
          setSelectedEtage(loc.id);
          const bu = await locationsApi.getByParent(loc.id);
          setBureaux(bu.data);
        } else if (loc.type_location === 'bureau') {
          setSelectedBureau(loc.id);
        }
      }
    } catch (err) {
      console.error('[useCascadeLocation] restoreFromLocationId error:', err);
    }
  };

  const isOffline = cachedLocationTree !== null;

  return {
    // State
    sites, batiments, etages, bureaux,
    selectedSite, selectedBatiment, selectedEtage, selectedBureau,
    selectedLocationId, locationPath, isOffline,
    // Setters
    setSelectedSite, setSelectedBatiment, setSelectedEtage, setSelectedBureau,
    // Actions
    loadSites, restoreFromLocationId,
  };
}
